/**
 * Helpers shared by the admin "Places" screen (register + edit a pharmacy).
 *
 * They live outside the component so the validation rules can be unit tested
 * without rendering the whole form.
 */

// The places are not only pharmacies, they are also imaging centers and labs.
export const PHARMACY_TYPES = [
  {
    label: "Pharmacy",
    value: "phar",
    description: "Places where the prescriptions are dispensed",
  },
  {
    label: "Diagnostic imaging places",
    value: "img",
    description: "Radiology and diagnostic imaging centers",
  },
  {
    label: "Laboratory Clinic",
    value: "lab",
    description: "Clinical analysis laboratories",
  },
];

export const PHARMACY_STATUSES = [
  { label: "Enabled", value: "enabled" },
  { label: "Disabled", value: "disabled" },
];

// Fields the admin must fill in before the place can be saved.
export const REQUIRED_PHARMACY_FIELDS = [
  "name",
  "email",
  "phone",
  "country_id",
  "city_id",
  "type",
  "directions",
];

// Label of every required field, used by the checklist of the form.
export const PHARMACY_FIELD_LABELS = {
  name: "Name",
  email: "E-mail",
  phone: "Phone number",
  country_id: "Country",
  city_id: "City",
  type: "Type",
  directions: "Directions",
};

const isFilled = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

/**
 * Returns the keys of the required fields that are still empty.
 *
 * @param {object} values the current form values
 * @returns {string[]}
 */
export const getMissingPharmacyFields = (values = {}) =>
  REQUIRED_PHARMACY_FIELDS.filter((field) => !isFilled(values[field]));

/**
 * True when every required field of the form has a value.
 *
 * @param {object} values the current form values
 * @returns {boolean}
 */
export const isPharmacyFormValid = (values = {}) =>
  getMissingPharmacyFields(values).length === 0;

/**
 * How much of the form is already filled in.
 *
 * @param {object} values the current form values
 * @returns {{missing: string[], completed: number, total: number, percent: number}}
 */
export const getPharmacyCompletion = (values = {}) => {
  const missing = getMissingPharmacyFields(values);
  const total = REQUIRED_PHARMACY_FIELDS.length;
  const completed = total - missing.length;

  return {
    missing,
    completed,
    total,
    percent: Math.round((completed / total) * 100),
  };
};

/**
 * Translates the type of a place to its label.
 *
 * @param {string} type one of phar | img | lab
 * @param {Function} t the i18n translate function
 * @returns {string}
 */
export const getPharmacyTypeLabel = (type, t) => {
  const found = PHARMACY_TYPES.find((item) => item.value === type);
  return found ? t(found.label) : type || "";
};

/**
 * Looks up the name of a country/city by its id.
 *
 * @param {Array<{id: number, name: string}>} list
 * @param {number|string} id
 * @returns {string}
 */
export const getNameById = (list, id) => {
  if (id === undefined || id === null || id === "") {
    return "";
  }
  const found = (list || []).find((item) => String(item.id) === String(id));
  return found ? found.name : "";
};

// ---------------------------------------------------------------------------
// Agreement code (código de convenio / código de descuento)
// ---------------------------------------------------------------------------

// The backend accepts the very same format: 1 to 50 characters made of
// letters, digits, hyphens and underscores.
export const AGREEMENT_CODE_MAX_LENGTH = 50;

const AGREEMENT_CODE_PATTERN = /^[A-Z0-9_-]+$/;

/**
 * Normalizes an agreement code: trims it and moves it to upper case.
 *
 * @param {string|null|undefined} value
 * @returns {string|null} the normalized code, null when there is no code
 */
export const normalizeAgreementCode = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  const normalized = String(value).trim().toUpperCase();
  return normalized === "" ? null : normalized;
};

/**
 * True when the code has the format accepted by the API.
 *
 * @param {string|null|undefined} value
 * @returns {boolean}
 */
export const isValidAgreementCode = (value) => {
  const normalized = normalizeAgreementCode(value);

  if (!normalized) {
    return false;
  }

  return (
    normalized.length <= AGREEMENT_CODE_MAX_LENGTH &&
    AGREEMENT_CODE_PATTERN.test(normalized)
  );
};

/**
 * The key that groups the branches of the same franchise.
 *
 * The admin names the branches "Farmacia Hospital Metropolitano [Lincoln
 * Plaza]", so everything before the first bracket identifies the franchise.
 * A place without brackets uses its whole name.
 *
 * @param {string|null|undefined} name
 * @returns {string} the normalized key, "" when there is no name
 */
export const getFranchiseKey = (name) => {
  if (name === undefined || name === null) {
    return "";
  }

  const base = String(name).split("[")[0].trim().toUpperCase();
  return base.replace(/\s+/g, " ");
};

/**
 * The places of `all` that belong to the same franchise as one of the
 * selected places and that are not selected yet.
 *
 * @param {Array<{id: number, name: string}>} selected
 * @param {Array<{id: number, name: string}>} all
 * @returns {Array<object>}
 */
export const getFranchiseMatches = (selected = [], all = []) => {  const selectedList = selected || [];
  const allList = all || [];

  const selectedIds = new Set(
    selectedList
      .filter((place) => place && place.id !== undefined && place.id !== null)
      .map((place) => String(place.id))
  );

  const selectedKeys = new Set(
    selectedList
      .map((place) => getFranchiseKey(place && place.name))
      .filter((key) => key !== "")
  );

  if (selectedKeys.size === 0) {
    return [];
  }

  return allList.filter((place) => {
    if (!place) {
      return false;
    }

    if (
      place.id !== undefined &&
      place.id !== null &&
      selectedIds.has(String(place.id))
    ) {
      return false;
    }

    const key = getFranchiseKey(place.name);
    return key !== "" && selectedKeys.has(key);
  });
};

/**
 * The places that really have an agreement code.
 *
 * Removing the code only affects the places that have one: telling the admin
 * that the code was removed from a place that never had it is misleading.
 *
 * @param {Array<{id: number, agreement_code: string}>} places
 * @returns {Array<object>}
 */
export const getPlacesWithAgreementCode = (places = []) =>
  (places || []).filter(
    (place) => place && !!normalizeAgreementCode(place.agreement_code)
  );
